import { useEffect, useRef, useState } from 'react';
import type { WorldMapNode } from '@goodtime/shared';
import {
  AudioBus,
  REALM_PLATE_TILES,
  OverviewScene,
  RealmMapScene,
  type RealmMapArt,
  type RealmPlacesApi,
  type RealmPlacesArt,
} from '@goodtime/engine';
import { LOBBY_MUSIC, SHARED_UI_ICONS } from '../../themes';
import { BUTTON_PLATES } from '../../themes/roadGames';
import { funnelBeacon } from '../../api/funnel';
import { claimSprings, fetchRealm, findSecret, makeWish, openPlace, playPlace } from '../../api/realm';
import { loadGameArt } from '../minigames/gameArt';
import { playApi } from '../minigames/registry';
import { iconForNode } from '../world/v2/BiomeListScreen';
import type { WalletAnchors } from '../BalancePill';
import { markRealmTitleSeen, realmTitleSeen } from './realmHome';
import { loadLitWorlds, saveLitWorlds, worldsPlayed } from './overviewHome';
import { REALM_FLOORS } from './realmFloors';
import { safeArea } from './safeArea';
import { WORLD_MAP_FADE_MS } from './WorldMap';
import { warmWorldMap } from './warmWorldMap';
import bezelUrl from '../../assets/road/light_bezel.png';
import arrowUrl from '../../assets/icon/back.png';
import lockUrl from '../../assets/icon/lock.png';
import gemUrl from '../../assets/icon/gem.png';
import friendsUrl from '../../assets/icon/friends.png';
import joinUrl from '../../assets/icon/join.png';
import clockUrl from '../../assets/icon/clock.png';
import coinUrl from '../../assets/icon/coin.png';
import sparkleUrl from '../../assets/icon/sparkle.png';
import giftUrl from '../../assets/icon/gift.png';
import crownUrl from '../../assets/map_node/boss.png';
import fountainUrl from '../../assets/realm_places/fountain.png';
import poolUrl from '../../assets/realm_places/pool.png';
import lighthouseUrl from '../../assets/realm_places/lighthouse.png';
import bridgeUrl from '../../assets/realm_places/bridge.png';
import oasisUrl from '../../assets/realm_places/oasis.png';
import pageTurnUrl from '../../assets/audio/realm/page_turn.mp3';
import placeUnlockUrl from '../../assets/audio/realm/place_unlock.mp3';
import secretSparkleUrl from '../../assets/audio/realm/secret_sparkle.mp3';

const ART: RealmMapArt = {
  bezel: bezelUrl,
  arrow: arrowUrl,
  lock: lockUrl,
  gem: gemUrl,
  friends: friendsUrl,
  join: joinUrl,
  schedule: clockUrl,
};

/** The side places' art (milestone 2): each place's painting, the secrets'
 *  glint, and the HUD's Wheel crown and gift. */
const PLACES_ART: RealmPlacesArt = {
  pictures: {
    fountain: fountainUrl,
    springs: poolUrl,
    lighthouse: lighthouseUrl,
    bridge: bridgeUrl,
    oasis: oasisUrl,
  },
  sparkle: sparkleUrl,
  coin: coinUrl,
  clock: clockUrl,
  crown: crownUrl,
  gift: giftUrl,
};

/** The places' sounds: a card opening, a place opening, a secret found. */
const REALM_AUDIO = [
  { alias: 'realm:page_turn', src: pageTurnUrl },
  { alias: 'realm:place_unlock', src: placeUnlockUrl },
  { alias: 'realm:secret_sparkle', src: secretSparkleUrl },
];

/** The ambient life's pieces and its spec (flag realm_alive). */
const AMBIENT_BASE = '/realm/ambient/v5';

/** Shared art loaded by alias: the painted button plates the boards'
 *  buttons sit on, and the coin and gem that fly to the wallet. */
const SHARED = [
  ...Object.values(BUTTON_PLATES),
  SHARED_UI_ICONS.coin!,
  SHARED_UI_ICONS.gem!,
].flatMap((e) => (e.src ? [{ alias: e.alias, src: e.src }] : []));

export interface RealmMapProps {
  unlockedSlotIds: ReadonlyArray<string>;
  /** The slot the player was last on: the map opens there. */
  currentNodeId: number;
  playerId?: string | null;
  /** The server session the daily games are played through. */
  sessionId?: string | null;
  /** The player's coins as shown. */
  coins?: number;
  /** Where the wallet's coin chip is (viewport pixels): coins fly there. */
  walletAnchors?: WalletAnchors | null;
  /** The server's wallet moved (a place opened, a prize landed). */
  onWalletChanged?: () => void;
  onPlayNode: (node: WorldMapNode) => void;
  onFriends: () => void;
  onJoinWithCode: () => void;
  onSchedule: () => void;
  /** The multiplayer Wheel for the world in view (index into BIOMES): the
   *  map's crown, as the old world map's crown. */
  onMainGame?: (world: number) => void;
  /** The daily gift is there to claim: the HUD shows it. */
  giftReady?: boolean;
  onGift?: () => void;
  /** Ambient life (flag realm_alive): the sea, ships, gulls, the serpent,
   *  smoke, steam, the fountains and the clouds move. Read when the map
   *  opens. */
  alive?: boolean;
  /** A dialog covers the map: ambient life pauses. */
  covered?: boolean;
  /** The overview of the worlds' own maps (flag world_maps): a tap in a
   *  world with a map glides in and opens it (`nodeId`: the building
   *  tapped, if one was). */
  overview?: { worlds: readonly number[]; onOpenWorld: (world: number, nodeId: number | null) => void };
  /** Back from a world's map: stay hidden under the map it replaces until
   *  the opening view has drawn (`onDrawn`), then show at once; that map
   *  fades out over it, and `onShown` follows once it has. */
  fadeIn?: boolean;
  onShown?: () => void;
  /** The opening view has drawn and the map is showing. */
  onDrawn?: () => void;
  /** The map could not start (a refused WebGL context, no memory) while it
   *  was hidden under the map it replaces: it has torn itself down, and the
   *  host keeps the player on that map. */
  onFailed?: () => void;
  /** The traveller's map strip (flag overview_strip) instead of the realm map: `opening` is the world it opens on, zoomed on its
   *  scene and easing out when the player is back from that world's map. It needs `overview`; read when the map opens. */
  strip?: { opening?: { world: number; zoomed: boolean } };
  /** This overview was a guess (the old one, before the player loaded) that the strip has replaced: it stays over the strip, out of
   *  reach, and fades out over STRIP_SWAP_MS. */
  fadeOut?: boolean;
}

/** How long the old overview fades out over the strip that replaces it. */
export const STRIP_SWAP_MS = 300;

/**
 * Hosts the realm map (flag `realm_map`): the Pixi {@link RealmMapScene},
 * on its own canvas in place of the episode list and the world maps. This
 * component mounts the scene, passes the player's slots through, and
 * connects the side places (milestone 2) to the server and the wallet.
 */
export function RealmMap({
  unlockedSlotIds,
  currentNodeId,
  playerId,
  sessionId,
  coins = 0,
  walletAnchors,
  onWalletChanged,
  onPlayNode,
  onFriends,
  onJoinWithCode,
  onSchedule,
  onMainGame,
  giftReady = false,
  onGift,
  alive = false,
  covered = false,
  overview,
  fadeIn = false,
  onShown,
  onDrawn,
  onFailed,
  strip,
  fadeOut = false,
}: RealmMapProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<RealmMapScene | OverviewScene | null>(null);
  const [shown, setShown] = useState(!fadeIn);
  const [failed, setFailed] = useState(false);
  const handlers = useRef({ onPlayNode, onFriends, onJoinWithCode, onSchedule, onGift, onWalletChanged, onMainGame, onOpenWorld: overview?.onOpenWorld, onShown, onDrawn, onFailed });
  handlers.current = { onPlayNode, onFriends, onJoinWithCode, onSchedule, onGift, onWalletChanged, onMainGame, onOpenWorld: overview?.onOpenWorld, onShown, onDrawn, onFailed };
  const unlockedRef = useRef(unlockedSlotIds);
  unlockedRef.current = unlockedSlotIds;
  // Read by the scene's calls when they're made, so they stay current.
  const live = useRef({ playerId, sessionId, coins, walletAnchors });
  live.current = { playerId, sessionId, coins, walletAnchors };

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    const need = <T,>(v: T | null | undefined, what: string): T => {
      if (v === null || v === undefined) throw new Error(`realm: no ${what} yet`);
      return v;
    };
    const pid = () => need(live.current.playerId, 'player');
    const api: RealmPlacesApi = {
      load: async () => (live.current.playerId ? fetchRealm(live.current.playerId) : null),
      open: (id) => openPlace(pid(), id),
      play: (id) => playPlace(pid(), id),
      claim: () => claimSprings(pid()),
      wish: () => makeWish(pid()),
      find: (id) => findSecret(pid(), id),
      playApi: (playId) => playApi(playId, need(live.current.sessionId, 'session')),
      loadGameArt,
      coins: () => live.current.coins,
      walletChanged: () => handlers.current.onWalletChanged?.(),
    };
    const common = {
      art: ART,
      floors: REALM_FLOORS,
      shared: SHARED,
      unlockedSlotIds: unlockedRef.current,
      currentNodeId,
      slotIcon: iconForNode,
      insets: safeArea(),
      onPlayNode: (node: WorldMapNode) => handlers.current.onPlayNode(node),
      onFriends: () => handlers.current.onFriends(),
      onJoinWithCode: () => handlers.current.onJoinWithCode(),
      onSchedule: () => handlers.current.onSchedule(),
    };
    const places = {
      api,
      art: PLACES_ART,
      showTitle: !realmTitleSeen(),
      onTitleSeen: markRealmTitleSeen,
      coinTarget: () => {
        const a = live.current.walletAnchors?.coin;
        if (!a) return null;
        const r = host.getBoundingClientRect();
        return { x: a.x - r.left, y: a.y - r.top };
      },
      onWheel: (world: number) => handlers.current.onMainGame?.(world),
      onGift: () => handlers.current.onGift?.(),
      giftReady,
    };
    const overviewOpts = overview
      ? {
          worlds: overview.worlds,
          onOpenWorld: (w: number, node: number | null) => handlers.current.onOpenWorld?.(w, node),
          onEnterWorld: warmWorldMap,
        }
      : undefined;
    const scene: RealmMapScene | OverviewScene =
      strip && overviewOpts
        ? new OverviewScene(host, {
            ...common,
            overview: overviewOpts,
            places,
            opening: strip.opening,
            lamps: { worldsPlayed, load: loadLitWorlds, save: saveLitWorlds },
          })
        : new RealmMapScene(host, {
            ...common,
            ambient: alive ? { base: AMBIENT_BASE, tiles: REALM_PLATE_TILES } : undefined,
            overview: overviewOpts,
            places,
          });
    sceneRef.current = scene;
    const dev = window as unknown as { __realmScene?: RealmMapScene | OverviewScene; __overviewScene?: OverviewScene };
    let fade: ReturnType<typeof setTimeout> | null = null;
    void scene
      .init()
      .then(() => (fadeIn ? scene.firstView() : undefined))
      .then(() => {
        if (sceneRef.current !== scene || !fadeIn) return;
        setShown(true);
        handlers.current.onDrawn?.();
        fade = setTimeout(() => handlers.current.onShown?.(), WORLD_MAP_FADE_MS + 20);
      })
      .catch((err) => {
        if (sceneRef.current !== scene) return;
        console.warn('[RealmMap] could not start:', err);
        // Only a map hidden under another is taken down (a map that opens on
        // its own has nothing to give way to): the host keeps the player on
        // the map it was to replace.
        if (!fadeIn) return;
        scene.destroy();
        sceneRef.current = null;
        if (dev.__realmScene === scene) delete dev.__realmScene;
        if (dev.__overviewScene === scene) delete dev.__overviewScene;
        setFailed(true);
        handlers.current.onFailed?.();
      });
    if (import.meta.env.DEV) {
      if (scene instanceof OverviewScene) dev.__overviewScene = scene;
      else dev.__realmScene = scene;
    }
    return () => {
      if (fade) clearTimeout(fade);
      scene.destroy();
      sceneRef.current = null;
      if (dev.__realmScene === scene) delete dev.__realmScene;
      if (dev.__overviewScene === scene) delete dev.__overviewScene;
    };
    // The map opens once per visit; later changes go through the scene.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    sceneRef.current?.setUnlockedSlotIds(unlockedSlotIds);
  }, [unlockedSlotIds]);

  useEffect(() => {
    sceneRef.current?.setAmbientPaused(covered);
  }, [covered]);

  useEffect(() => {
    sceneRef.current?.setGiftReady(giftReady);
  }, [giftReady]);

  // world_maps can arrive with the player, after the map has opened.
  const overviewWorlds = overview?.worlds.join() ?? null;
  useEffect(() => {
    const worlds = overview?.worlds;
    const scene = sceneRef.current;
    // The strip is the overview already; only the realm map switches the mode.
    if (!(scene instanceof RealmMapScene)) return;
    scene.setOverview(
      worlds
        ? { worlds, onOpenWorld: (w, node) => handlers.current.onOpenWorld?.(w, node), onEnterWorld: warmWorldMap }
        : undefined,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overviewWorlds]);

  // The map can open before the player has loaded (it remembers it's
  // home): their places come once they have.
  const loadedFor = useRef(playerId);
  useEffect(() => {
    if (playerId && playerId !== loadedFor.current) sceneRef.current?.reloadPlaces();
    loadedFor.current = playerId;
  }, [playerId]);

  // Lobby music, as on the world maps; the slot cross-fades to its own.
  // The places' own sounds load with the map.
  useEffect(() => {
    void AudioBus.shared.loadEntry(LOBBY_MUSIC.alias, LOBBY_MUSIC.src);
    AudioBus.shared.playMusic(LOBBY_MUSIC.alias, { fadeInMs: 500 });
    for (const s of REALM_AUDIO) void AudioBus.shared.loadEntry(s.alias, s.src).catch(() => undefined);
    funnelBeacon('map_opened', { playerId, detail: strip ? 'strip' : 'realm' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (failed) return null;
  return (
    <div
      ref={hostRef}
      data-testid="realm-map"
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        // The strip's parchment, until its painting is in.
        background: strip ? '#dac28c' : '#3f9fd0',
        touchAction: 'none',
        // Under the map it replaces, which fades out over it: no fade of its own.
        opacity: shown && !fadeOut ? 1 : 0,
        // The guess the strip replaces fades away over it, and takes no taps meanwhile.
        transition: fadeOut ? `opacity ${STRIP_SWAP_MS}ms linear` : undefined,
        pointerEvents: fadeOut ? 'none' : undefined,
      }}
    />
  );
}
